import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight, Check, Download, ChevronRight, ChevronLeft, X,
  Shield, MoveHorizontal, Droplets, Sparkles, Ship, Settings2, Cpu, Maximize2,
  Ruler, Award, Building2, GitCompare, MapPin, FileText, Gauge, Layers, Zap, Wrench,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import CataloguePreview from "@/components/CataloguePreview";
import WhatsAppQuoteButton from "@/components/WhatsAppQuoteButton";
import {
  getProductBySlug, getCategoryBySlug, getProductsByCategory,
} from "@/lib/catalog";
import { IMAGES } from "@/lib/site";

const featureIconMap = {
  Shield, MoveHorizontal, Droplets, Sparkles, Ship, Settings2, Cpu, Maximize2, Award,
};

export default function ProductDetail() {
  const { slug, category: categorySlug } = useParams();
  const product = getProductBySlug(slug);
  const category = product ? getCategoryBySlug(product.category) : null;
  const variants = product ? getProductsByCategory(product.category) : [];
  const related = variants.filter((p) => p.slug !== slug).slice(0, 3);

  const [lbIdx, setLbIdx] = useState(null);
  const lbOpen = lbIdx !== null;
  const [compareOpen, setCompareOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
  const onScroll = () => setStickyVisible(window.scrollY > 600);
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
  return () => window.removeEventListener("scroll", onScroll);
}, []);



  useEffect(() => {
    const onScroll = () => setStickyVisible(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!product) return;
    const url = typeof window !== "undefined" ? window.location.href : "";
    const image = product.images?.[0] || product.thumbnail || "";

    document.title = product.seo_title;

    const setMeta = (selector, attr, key, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("data-stenzia-seo", "1");
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta('meta[name="description"]', "name", "description", product.seo_description);
    setMeta('meta[name="keywords"]', "name", "keywords", product.meta_keywords || "");

    // OpenGraph
    setMeta('meta[property="og:type"]', "property", "og:type", "product");
    setMeta('meta[property="og:title"]', "property", "og:title", product.og_title || product.seo_title);
    setMeta('meta[property="og:description"]', "property", "og:description", product.og_description || product.seo_description);
    setMeta('meta[property="og:image"]', "property", "og:image", image);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[property="og:site_name"]', "property", "og:site_name", "Stenzia Displays");

    // Twitter card
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", product.og_title || product.seo_title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", product.og_description || product.seo_description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.setAttribute("data-stenzia-seo", "1");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // JSON-LD Product schema
    const schema = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      alternateName: product.technical_name,
      description: product.seo_description,
      image: product.images && product.images.length ? product.images : [image],
      brand: { "@type": "Brand", name: "Stenzia Displays" },
      manufacturer: { "@type": "Organization", name: "Stenzia Displays LLP", url: "https://stenziadisplays.com/" },
      category: category?.name || "Tile Display Systems",
      countryOfOrigin: "IN",
      url,
      ...(product.material ? { material: product.material } : {}),
      additionalProperty: Object.entries(product.specifications || {}).map(([k, v]) => ({
        "@type": "PropertyValue",
        name: k.replace(/_/g, " "),
        value: String(v),
      })),
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        priceCurrency: "INR",
        price: "0",
        priceSpecification: { "@type": "PriceSpecification", priceCurrency: "INR", description: "Request for quote" },
        seller: { "@type": "Organization", name: "Stenzia Displays LLP" },
        url,
      },
    };

    let ld = document.head.querySelector('script[data-stenzia-seo="ld"]');
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute("data-stenzia-seo", "ld");
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(schema);

    return () => {
      // Clean up dynamic OG/Twitter/canonical/JSON-LD when navigating away
      document.head
        .querySelectorAll('[data-stenzia-seo]')
        .forEach((el) => {
          if (el.tagName === "META" && (el.getAttribute("name") === "description" || el.getAttribute("name") === "keywords")) {
            // Leave description/keywords for next page's hook to overwrite
            return;
          }
          el.parentNode && el.parentNode.removeChild(el);
        });
    };
  }, [product, category]);

  useEffect(() => {
    if (!lbOpen) return;
    const onKey = (e) => {
      const list = product.images;
      if (e.key === "Escape") setLbIdx(null);
      if (e.key === "ArrowRight") setLbIdx((i) => (i + 1) % list.length);
      if (e.key === "ArrowLeft") setLbIdx((i) => (i - 1 + list.length) % list.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lbOpen, product]);

  if (!product) return <Navigate to="/products" replace />;
  if (categorySlug && product.category !== categorySlug)
    return <Navigate to={`/products/${product.category}/${product.slug}`} replace />;

  const heroImage =
  product.hero ||
  product.images?.[0] ||
  product.thumbnail;
  const galleryImages = product.images && product.images.length > 1 ? product.images : [product.thumbnail];

  return (
    <div data-testid="product-detail-page">
      {/* PREMIUM HERO with series + technical name */}
      <PageHero
        overline={product.series || category?.name}
        title={product.name}
        subtitle={product.technical_name || product.short}
        image={heroImage}
      />

      {/* Badges + breadcrumb */}
      <div className="bg-white border-b border-charcoal/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          <Link to="/products" className="text-charcoal/55 hover:text-stenzia-green transition-colors">Products</Link>
          <ChevronRight size={12} className="text-charcoal/30" />
          <Link to="/products" className="text-charcoal/55 hover:text-stenzia-green transition-colors">{category?.name}</Link>
          <ChevronRight size={12} className="text-charcoal/30" />
          <span className="text-charcoal">{product.name}</span>

          <div className="ml-auto flex items-center gap-2">
            {product.best_seller && <span className="px-2.5 py-0.5 rounded-full bg-stenzia-gold/15 text-stenzia-gold text-[10px] tracking-[0.18em] uppercase font-semibold">Bestseller</span>}
            {product.new_arrival && <span className="px-2.5 py-0.5 rounded-full bg-stenzia-green/15 text-stenzia-green text-[10px] tracking-[0.18em] uppercase font-semibold">New</span>}
            {product.featured && <span className="px-2.5 py-0.5 rounded-full bg-charcoal text-white text-[10px] tracking-[0.18em] uppercase font-semibold">Featured</span>}
          </div>
        </div>
      </div>

      {/* Variant selector — premium product cards */}
      {variants.length > 1 && (
        <section className="bg-white border-b border-charcoal/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-10">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div className="overline text-stenzia-green"><span className="kbd-line" /> Choose your model</div>
              <button
                type="button"
                onClick={() => setCompareOpen(true)}
                data-testid="compare-models-btn"
                className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-charcoal/15 text-xs tracking-wide uppercase text-charcoal/70 hover:border-stenzia-green hover:text-stenzia-green hover:bg-stenzia-green/5 transition-all"
              >
                <GitCompare size={14} />
                Compare Models
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {variants.map((v) => {
                const active = v.slug === product.slug;
                return (
                  <Link
                    key={v.slug}
                    to={`/products/${v.category}/${v.slug}`}
                    data-testid={`variant-card-${v.slug}`}
                    className={`relative block rounded-2xl p-5 md:p-6 border transition-all duration-400 ${
                      active
                        ? "border-stenzia-green bg-stenzia-green/[0.04] shadow-[0_18px_36px_-18px_rgba(11,107,83,0.35)]"
                        : "border-charcoal/10 bg-white hover:border-stenzia-green/40 hover:-translate-y-0.5 hover:shadow-[0_14px_28px_-14px_rgba(0,0,0,0.18)]"
                    }`}
                  >
                    {active && <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-stenzia-green" />}
                    <div className="text-[10px] tracking-[0.22em] uppercase text-charcoal/45 font-semibold">{v.model_designation || "Model"}</div>
                    <div className={`mt-2 font-display text-xl md:text-2xl tracking-tight ${active ? "text-stenzia-green" : "text-charcoal"}`}>{v.name}</div>
                    <div className="mt-2 text-xs text-charcoal/60 leading-relaxed line-clamp-2">{v.technical_name}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* OVERVIEW — image left · sectioned content right */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] bg-[#F6F7F5] shadow-xl">
             <img
  src={product.images?.[0] || product.thumbnail}
  alt={product.name}
  className="absolute inset-0 w-full h-full object-cover"
/>
            </div>
            {/* Engineering Highlights — 4 cards */}
            {product.highlights?.length > 0 && (
              <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
                {product.highlights.map((h) => {
                  const Icon = featureIconMap[h.icon] || Shield;
                  return (
                    <div key={h.title} className="p-4 rounded-2xl bg-[#F6F7F5] border border-charcoal/8 hover:border-stenzia-green/25 transition-colors">
                      <Icon size={18} className="text-stenzia-green" />
                      <div className="mt-2 font-display text-sm text-charcoal tracking-tight">{h.title}</div>
                      <div className="mt-1 text-[11px] text-charcoal/55 leading-snug">{h.body}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Reveal>

          <Reveal delay={1} className="lg:col-span-5 space-y-8">
            <div>
              <div className="overline text-stenzia-green"><span className="kbd-line" /> Overview</div>
              <p className="mt-4 text-charcoal/75 leading-relaxed">{product.overview || product.description}</p>
            </div>
            {product.engineering_description && (
              <div>
                <div className="overline text-stenzia-green">Engineering</div>
                <p className="mt-3 text-charcoal/70 leading-relaxed text-sm md:text-base">{product.engineering_description}</p>
              </div>
            )}
            {product.construction && (
              <div>
                <div className="overline text-stenzia-green">Construction</div>
                <p className="mt-3 text-charcoal/70 leading-relaxed text-sm md:text-base">{product.construction}</p>
              </div>
            )}

            {product.best_for?.length > 0 && (
              <div>
                <div className="overline text-stenzia-green">Best For</div>
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {product.best_for.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm md:text-base text-charcoal/75">
                      <ChevronRight size={14} className="text-stenzia-green mt-1 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.why_choose?.length > 0 && (
              <div>
                <div className="overline text-stenzia-green">Why Choose {product.name}</div>
                <ul className="mt-3 space-y-2">
                  {product.why_choose.map((w) => (
                    <li key={w} className="flex items-start gap-2.5 text-sm md:text-base text-charcoal/80">
                      <Check size={15} className="text-stenzia-green mt-1 shrink-0" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <Link to={`/contact?product=${product.slug}`} data-testid="product-request-quote" className="group btn-primary text-sm" style={{ padding: "12px 24px" }}>
                Request Quote <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <WhatsAppQuoteButton product={product} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ENGINEERING SPECIFICATIONS — 2-column table */}
      <section className="py-16 md:py-24 bg-[#F6F7F5]" data-testid="spec-table">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Engineering Specifications</div>
            <h3 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal">Technical data sheet.</h3>
          </Reveal>
          <div className="mt-10 bg-white rounded-2xl border border-charcoal/10 overflow-hidden divide-y divide-charcoal/10">
            {Object.entries(product.specifications).map(([k, v], i) => (
              <div key={k} className={`grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-6 px-6 py-4 ${i % 2 === 1 ? "bg-[#FAFAFA]" : ""}`}>
                <dt className="text-[11px] tracking-[0.22em] uppercase text-charcoal/55 font-semibold">{k.replace(/_/g, " ")}</dt>
                <dd className="text-sm md:text-base text-charcoal">{v}</dd>
              </div>
            ))}
          </div>

          {/* Panel sizes + Configurations + Colours */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {product.tile_sizes?.length > 0 && (
              <div className="bg-white rounded-2xl border border-charcoal/10 p-6">
                <div className="overline text-stenzia-green">Available Panel Sizes</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {product.tile_sizes.map((s) => (
                    <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-[#F6F7F5] border border-charcoal/10 text-charcoal/80">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {product.configurations?.length > 0 && (
              <div className="bg-white rounded-2xl border border-charcoal/10 p-6">
                <div className="overline text-stenzia-green">Available Configurations</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {product.configurations.map((c) => (
                    <span key={c} className="text-xs px-3 py-1.5 rounded-full bg-stenzia-green/[0.06] border border-stenzia-green/20 text-stenzia-green font-medium">{c}</span>
                  ))}
                </div>
                <div className="mt-4 text-[11px] text-charcoal/50">Configuration depends on panel size.</div>
              </div>
            )}
            {product.colours?.length > 0 && (
              <div className="bg-white rounded-2xl border border-charcoal/10 p-6">
                <div className="overline text-stenzia-green">Available Colours</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {product.colours.map((c) => (
                    <span key={c} className="text-xs px-3 py-1.5 rounded-full bg-[#F6F7F5] border border-charcoal/10 text-charcoal/80">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Standard features checklist */}
          {product.standard_features?.length > 0 && (
            <div className="mt-6 bg-white rounded-2xl border border-charcoal/10 p-6 md:p-8">
              <div className="overline text-stenzia-green">Standard Features</div>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                {product.standard_features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-charcoal/80">
                    <Check size={16} className="text-stenzia-green mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* APPLICATIONS — icon cards */}
      {product.applications?.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <div className="overline text-stenzia-green"><span className="kbd-line" /> Applications</div>
              <h3 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal max-w-2xl">Where this system performs best.</h3>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {product.applications.map((a, i) => (
                <Reveal key={a} delay={i % 6}>
                  <div className="h-full bg-[#F6F7F5] p-5 rounded-2xl border border-transparent hover:border-stenzia-green/20 hover:bg-white hover:shadow-md transition-all duration-400">
                    <div className="w-9 h-9 rounded-lg bg-stenzia-green/10 text-stenzia-green flex items-center justify-center">
                      <Building2 size={16} />
                    </div>
                    <div className="mt-3 font-display text-sm text-charcoal tracking-tight leading-snug">{a}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 4. KEY FEATURES */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Key Features</div>
            <h3 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal max-w-2xl">Engineered into every unit.</h3>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {product.key_features.map((f, i) => {
              const Icon = featureIconMap[f.icon] || Shield;
              return (
                <Reveal key={f.title} delay={i % 3}>
                  <div className="group h-full bg-[#F6F7F5] rounded-3xl p-8 border border-transparent hover:border-stenzia-green/20 hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
                    <div className="w-12 h-12 rounded-xl bg-stenzia-green text-white flex items-center justify-center group-hover:rotate-6 group-hover:scale-110 transition-all duration-500 shadow-lg shadow-stenzia-green/20">
                      <Icon size={20} />
                    </div>
                    <h4 className="mt-5 font-display text-lg text-charcoal tracking-tight">{f.title}</h4>
                    <p className="mt-2 text-sm text-charcoal/65 leading-relaxed">{f.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. PRODUCT GALLERY */}
      {/*galleryImages.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F6F7F5]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <div className="overline text-stenzia-green"><span className="kbd-line" /> Product Gallery</div>
              <h3 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal">A closer look.</h3>
            </Reveal>
            <div className="mt-10 columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
              {galleryImages.map((img, i) => (
                <Reveal key={i} delay={i % 4} className="break-inside-avoid">
                  <button
                    type="button"
                    onClick={() => setLbIdx(i)}
                    data-testid={`pd-gallery-${i}`}
                    className="gallery-card relative w-full overflow-hidden rounded-2xl aspect-[4/5] group cursor-pointer focus:outline-none"
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[450ms] ease-[cubic-bezier(.2,.7,.2,1)] will-change-transform group-hover:scale-[1.07]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/75 via-charcoal/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-[420ms]" />
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#0B6B53] text-white flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(11,107,83,0.7)] opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-[420ms]">
                      <ArrowUpRight size={18} strokeWidth={2.2} />
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )*/}

      {/* 6. TECHNICAL DRAWINGS */}
      {product.drawings.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <div className="overline text-stenzia-green"><span className="kbd-line" /> Technical Drawings</div>
              <h3 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal">CAD, dimensions and views.</h3>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {product.drawings.map((d, i) => (
                <Reveal key={d.label} delay={i}>
                  <a href={d.url || "#"} target="_blank" rel="noopener noreferrer" className="group block bg-[#F6F7F5] rounded-2xl p-6 border border-transparent hover:border-stenzia-green/25 hover:shadow-xl hover:-translate-y-1 transition-all duration-500">
                    <div className="aspect-[4/3] mb-5 rounded-xl bg-white border border-charcoal/10 flex items-center justify-center overflow-hidden">
                      {d.preview ? (
                        <img src={d.preview} alt={d.label} className="w-full h-full object-contain p-2" />
                      ) : (
                        <Ruler size={32} className="text-charcoal/30" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-stenzia-green font-semibold">{d.type || "Drawing"}</div>
                        <div className="mt-1 font-display text-base text-charcoal">{d.label}</div>
                      </div>
                      <Download size={16} className="text-charcoal/40 group-hover:text-stenzia-green transition-colors" />
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7. COMPANY CATALOGUE — single unified button */}
      <section className="py-16 md:py-24 bg-[#F6F7F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-3xl border border-charcoal/10 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <div className="overline text-stenzia-green"><span className="kbd-line" /> Company Catalogue</div>
              <h3 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal leading-[1.05]">
                Company Catalogue.
              </h3>
              <p className="mt-4 text-sm md:text-base text-charcoal/65 leading-relaxed">
                Download the complete STENZIA Displays catalogue containing every product series.
              </p>
            </div>
            <CataloguePreview buttonLabel="Download Catalogue" testId="download-catalogue-trigger" />
          </div>
        </div>
      </section>

      {/* 8. RELATED */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <Reveal>
                <div className="overline text-stenzia-green"><span className="kbd-line" /> Related Products</div>
                <h3 className="mt-5 font-display font-light text-3xl tracking-tighter text-charcoal">More in {category?.name}.</h3>
              </Reveal>
              <Link to="/products" className="text-sm text-stenzia-green hover:underline">View all products →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/products/${r.category}/${r.slug}`} data-testid={`related-${r.slug}`} className="group block rounded-2xl overflow-hidden bg-[#F6F7F5] border border-charcoal/10 hover:border-stenzia-green/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-500">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={r.thumbnail} alt={r.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  </div>
                  <div className="p-6">
                    <h4 className="font-display text-lg text-charcoal tracking-tight">{r.name}</h4>
                    <p className="mt-2 text-sm text-charcoal/65 leading-relaxed">{r.short}</p>
                    <div className="mt-4 text-sm text-stenzia-green inline-flex items-center gap-1 group-hover:gap-2 transition-all">View product <ArrowUpRight size={14} /></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 9. REQUEST QUOTE CTA */}
      <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-stenzia-green/15 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <div className="overline text-stenzia-gold">Request a Quote</div>
            <h3 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.04]">
              Need a custom configuration?
            </h3>
            <p className="mt-6 text-white/65 max-w-xl mx-auto leading-relaxed">Share your showroom requirements — our engineering team will respond within 24 hours with concept drawings and indicative pricing.</p>
            <Link to={`/contact?product=${product.slug}`} className="group btn-primary mt-10 inline-flex">
              Request Quote <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PROUDLY MADE IN INDIA */}
      <section className="py-16 md:py-24 bg-white" data-testid="made-in-india">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Origin</div>
            <h3 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal leading-[1.05]">
              Proudly Designed &amp; Manufactured in India.
            </h3>
            <p className="mt-5 text-charcoal/65 leading-relaxed text-sm md:text-base">
              Every STENZIA display system is engineered and manufactured in India using premium materials, precision fabrication and advanced manufacturing processes. Built for Indian and global ceramic markets.
            </p>
            <div className="mt-6 text-xs text-charcoal/55 italic">
              Engineered for long-term showroom performance using premium materials and precision manufacturing.
            </div>
          </Reveal>
          <Reveal delay={1} className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden aspect-[16/10] bg-charcoal shadow-xl">
              <img src={IMAGES.heroAlt || product.thumbnail} alt="Made in India" className="absolute inset-0 w-full h-full object-cover opacity-80" />
              <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/70 via-charcoal/20 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-stenzia-gold/90 text-charcoal text-[10px] tracking-[0.22em] uppercase font-semibold">
                  <MapPin size={12} /> Morbi · Gujarat · India
                </div>
                <div className="mt-4 font-display text-2xl md:text-3xl tracking-tight">
                  Engineered in India. Trusted globally.
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY STENZIA DISPLAYS — reusable block on every product page */}
      <section className="py-16 md:py-24 bg-[#F6F7F5]" data-testid="why-stenzia">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="overline text-stenzia-green"><span className="kbd-line" /> Why Stenzia</div>
            <h3 className="mt-5 font-display font-light text-3xl md:text-4xl tracking-tighter text-charcoal leading-[1.05]">
              Why Stenzia Displays.
            </h3>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Shield, title: "Heavy Duty Engineering", body: "NIZONE CRC steel, FEA-verified load capacity and CAD-driven design." },
              { icon: Layers, title: "Premium Raw Materials", body: "Premium plywood, SS 202 hardware, industrial-grade adhesives and bonded carpet." },
              { icon: Gauge, title: "Precision Manufacturing", body: "Jig-welded chassis, 7-tank pre-treatment and 27-point QC inspection on every unit." },
              { icon: Wrench, title: "Custom Built Solutions", body: "Bespoke sizing, colour, branding and configuration — built to your exact spec." },
              { icon: MapPin, title: "Made in India", body: "Designed and manufactured in our Morbi factory — the world's second-largest ceramic hub." },
              { icon: Zap, title: "Long-Term Showroom Performance", body: "Engineered for continuous daily use in flagship ceramic showrooms worldwide." },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 border border-charcoal/10 hover:border-stenzia-green/25 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-400">
                <div className="w-11 h-11 rounded-xl bg-stenzia-green/10 text-stenzia-green flex items-center justify-center">
                  <f.icon size={18} />
                </div>
                <div className="mt-4 font-display text-base text-charcoal tracking-tight">{f.title}</div>
                <div className="mt-1.5 text-[13px] text-charcoal/60 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARE MODELS MODAL */}
      <AnimatePresence>
        {compareOpen && variants.length > 1 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
            data-testid="compare-modal"
            className="fixed inset-0 z-[75] bg-charcoal/85 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-8"
            onClick={() => setCompareOpen(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full md:max-w-6xl bg-white rounded-t-3xl md:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden"
            >
              <div className="flex items-center justify-between gap-4 px-6 md:px-10 py-5 border-b border-charcoal/10">
                <div>
                  <div className="overline text-stenzia-green">Compare Models</div>
                  <div className="mt-1 font-display text-xl md:text-2xl tracking-tight text-charcoal">{category?.name}</div>
                </div>
                <button onClick={() => setCompareOpen(false)} data-testid="compare-close" className="w-10 h-10 rounded-full bg-[#F6F7F5] hover:bg-charcoal hover:text-white text-charcoal flex items-center justify-center transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="overflow-auto px-2 md:px-6 py-6">
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead>
                    <tr className="text-left">
                      <th className="sticky left-0 bg-white px-3 py-3 text-[10px] uppercase tracking-[0.22em] text-charcoal/50 font-semibold w-[180px]">Specification</th>
                      {variants.map((v) => (
                        <th key={v.slug} className="px-3 py-3 align-bottom min-w-[180px]">
                          <div className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50 font-semibold">{v.model_designation}</div>
                          <div className="mt-1 font-display text-base text-charcoal tracking-tight">{v.name}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const keys = Array.from(new Set(variants.flatMap((v) => Object.keys(v.specifications || {}))));
                      return keys.map((k, idx) => (
                        <tr key={k} className={idx % 2 ? "bg-[#F6F7F5]" : "bg-white"}>
                          <td className="sticky left-0 bg-inherit px-3 py-3 text-xs uppercase tracking-wide text-charcoal/55 font-medium align-top">{k.replace(/_/g, " ")}</td>
                          {variants.map((v) => (
                            <td key={v.slug} className="px-3 py-3 text-[13px] text-charcoal/85 align-top leading-snug">
                              {v.specifications?.[k] || <span className="text-charcoal/30">—</span>}
                            </td>
                          ))}
                        </tr>
                      ));
                    })()}
                    <tr>
                      <td className="px-3 py-4"></td>
                      {variants.map((v) => (
                        <td key={v.slug} className="px-3 py-4">
                          <Link
                            to={`/products/${v.category}/${v.slug}`}
                            onClick={() => setCompareOpen(false)}
                            data-testid={`compare-view-${v.slug}`}
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-stenzia-green font-medium hover:text-charcoal transition-colors"
                          >
                            View {v.model_designation} <ArrowUpRight size={12} />
                          </Link>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* STICKY CTA BAR — appears after hero scroll */}
      <AnimatePresence>
        {stickyVisible && !isMobile && (
          <motion.div
            initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="sticky-cta"
className="hidden md:flex fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-charcoal text-white rounded-2xl shadow-2xl border border-white/10 backdrop-blur-md p-3 w-[calc(100vw-1.5rem)] max-w-md sm:w-auto sm:max-w-[calc(100vw-2rem)] flex-col sm:flex-row items-stretch sm:items-center gap-2"          >
            <div className="hidden sm:block pl-3 pr-2 text-xs">
              <span className="text-white/55 uppercase tracking-wider text-[10px]">{category?.name?.replace("STENZIA ", "")}</span>
              <span className="mx-2 text-white/30">·</span>
              <span className="text-white">{product.name}</span>
            </div>
            <a
              href="#engineering-specifications"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector('[data-testid="spec-table"]');
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="sticky-specs"
              className="inline-flex justify-center items-center gap-1.5 w-full sm:w-auto px-3 py-3 rounded-full text-sm sm:text-xs hover:bg-white/10 transition-colors"            >
              <FileText size={13} /> Specifications
            </a>
            <Link
              to={`/contact?product=${product.slug}`}
              data-testid="sticky-quote"
              className="inline-flex justify-center items-center gap-1.5 w-full sm:w-auto px-4 py-3 rounded-full bg-stenzia-green text-white text-sm sm:text-xs font-medium hover:bg-white hover:text-charcoal transition-colors"            >
              Request Quote <ArrowUpRight size={12} />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lbOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            onClick={() => setLbIdx(null)} data-testid="pd-lightbox"
            className="fixed inset-0 z-[70] bg-charcoal/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
          >
            <button data-testid="pd-lightbox-close" onClick={(e) => { e.stopPropagation(); setLbIdx(null); }} className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white/10 hover:bg-white text-white hover:text-charcoal flex items-center justify-center transition-all"><X size={20} /></button>
            {galleryImages.length > 1 && (<>
              <button onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i - 1 + galleryImages.length) % galleryImages.length); }} className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-stenzia-gold text-white hover:text-charcoal flex items-center justify-center transition-all"><ChevronLeft size={20} /></button>
              <button onClick={(e) => { e.stopPropagation(); setLbIdx((i) => (i + 1) % galleryImages.length); }} className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-stenzia-gold text-white hover:text-charcoal flex items-center justify-center transition-all"><ChevronRight size={20} /></button>
            </>)}
            <motion.div key={lbIdx} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.35 }} className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={galleryImages[lbIdx]} alt="" className="w-full max-h-[78vh] object-contain rounded-2xl shadow-2xl" />
              <div className="mt-4 text-center text-white/70 text-sm">{lbIdx + 1} / {galleryImages.length}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
